import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { INVENTORY_API_URL } from '../../constants';
import { activityFromMovementLog } from '../../domain/activityLog';
import { displayMaterialLineKindFromApi } from '../../domain/procurementRequest';
import { ActivityTable } from '../activity/ActivityTable';
import { SourcePoRequestLinks, type SourcePoRequestFields } from '../sourceLinks/SourcePoRequestLinks';

type RegionField = {
  id?: string;
  name?: string;
  county?: string;
  state?: string;
  closestCity?: string;
};

type InventoryItemDetail = {
  id?: string;
  inventoryNumber?: string;
  materialLineKind?: string;
  sku?: string;
  materialSpecification?: string;
  region?: string;
  currentRegionField?: RegionField;
  currentDeliveryAddressId?: string | null;
  currentDeliveryAddress?: string;
  afeNumber?: string;
  trackFootage?: boolean;
  quantityOnHand?: number;
  footageOnHand?: number | null;
  source?: string;
  movementLog?: { atUtc?: string; summary?: string; materialTransferNumber?: string | null }[];
} & SourcePoRequestFields;

type MaterialTransferLine = {
  materialLineKind?: string;
  sku?: string;
  inventoryItemId?: string;
  lineCondition?: string;
  quantity?: number | null;
  footage?: number | null;
};

type MaterialTransferSummary = {
  id?: string;
  transferNumber?: string;
  status?: string;
  region?: string;
  movementDateUtc?: string;
  lines?: MaterialTransferLine[];
};

function formatMtStatus(s: string | undefined): string {
  const t = (s ?? '').trim();
  if (!t) {
    return '—';
  }
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function InventoryItemDetailPanel(props: {
  inventoryId: string;
  onClose: () => void;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  onOpenMaterialTransfer: (id: string) => void;
}): ReactElement {
  const { inventoryId, onClose, authFetch, onOpenMaterialTransfer } = props;
  const [item, setItem] = useState<InventoryItemDetail | null>(null);
  const [transfers, setTransfers] = useState<MaterialTransferSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    setItem(null);
    setTransfers(null);
    const base = `${INVENTORY_API_URL}/${encodeURIComponent(inventoryId)}`;
    try {
      const [a, b] = await Promise.all([
        authFetch(base),
        authFetch(`${base}/material-transfers`)
      ]);
      if (!a.ok) {
        setErr(a.status === 404 ? 'This inventory item was not found or is outside your regions.' : 'Could not load item.');
        return;
      }
      const it = (await a.json()) as InventoryItemDetail;
      setItem(it);
      if (b.ok) {
        const t = (await b.json()) as MaterialTransferSummary[];
        setTransfers(Array.isArray(t) ? t : []);
      } else {
        setTransfers([]);
      }
    } catch {
      setErr('Could not load inventory item.');
    } finally {
      setLoading(false);
    }
  }, [authFetch, inventoryId]);

  useEffect(() => {
    void load();
  }, [load]);

  const title = item?.inventoryNumber?.trim() || 'Inventory item';

  return (
    <aside className="side-panel" aria-label="Inventory item details">
      <div className="side-panel-header">
        <h3>{title}</h3>
        <div className="side-panel-actions">
          <button type="button" className="icon-close" onClick={onClose} aria-label="Close panel">
            ×
          </button>
        </div>
      </div>
      <div className="side-panel-body">
        {loading && <p className="hint">Loading…</p>}
        {err && <p className="danger-text">{err}</p>}
        {!loading && !err && item && (
          <>
            <dl className="detail-list">
              <dt>Kind</dt>
              <dd>{displayMaterialLineKindFromApi(item.materialLineKind)}</dd>
              <dt>SKU</dt>
              <dd className="mono-id">{item.sku ?? '—'}</dd>
              <dt>Specification</dt>
              <dd className="multiline">{item.materialSpecification?.trim() || '—'}</dd>
              <dt>On hand</dt>
              <dd>
                {item.trackFootage
                  ? `${item.footageOnHand ?? 0} ft`
                  : `${item.quantityOnHand ?? 0} qty`}
              </dd>
              <dt>Source</dt>
              <dd>{item.source != null && item.source !== '' ? String(item.source) : '—'}</dd>
              <dt>Region</dt>
              <dd>{item.region ?? '—'}</dd>
              <dt>Field</dt>
              <dd>
                {item.currentRegionField?.name?.trim() || item.currentRegionField?.id || '—'}
                {item.currentRegionField?.county || item.currentRegionField?.state
                  ? ` (${[item.currentRegionField?.county, item.currentRegionField?.state]
                      .filter(Boolean)
                      .join(', ')})`
                  : ''}
              </dd>
              <dt>Delivery address</dt>
              <dd className="multiline">{item.currentDeliveryAddress?.trim() || '—'}</dd>
              <dt>AFE #</dt>
              <dd>{item.afeNumber?.trim() || '—'}</dd>
            </dl>
            <SourcePoRequestLinks
              sourcePurchaseOrderId={item.sourcePurchaseOrderId}
              sourcePurchaseOrderNumber={item.sourcePurchaseOrderNumber}
              sourceProcurementRequestId={item.sourceProcurementRequestId}
              sourceRequestNumber={item.sourceRequestNumber}
            />

            <h4>Material Transfers (lines referencing this lot)</h4>
            {transfers && transfers.length === 0 && (
              <p className="hint">No Material Transfers include this lot yet.</p>
            )}
            {transfers && transfers.length > 0 && (
              <ul className="mt-transfer-line-list">
                {transfers.map((t) => {
                  const line = (t.lines ?? []).find(
                    (l) => (l.inventoryItemId ?? '').trim() === (item.id ?? inventoryId)
                  );
                  return (
                    <li key={t.id ?? t.transferNumber}>
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => t.id && onOpenMaterialTransfer(t.id)}
                        disabled={!t.id}
                      >
                        {t.transferNumber ?? t.id}
                      </button>
                      <span className="hint"> — {formatMtStatus(t.status)}</span>
                      {line && (
                        <div className="hint" style={{ fontSize: '0.88rem' }}>
                          Line: {line.lineCondition ?? '—'}
                          {line.quantity != null ? `, qty ${line.quantity}` : ''}
                          {line.footage != null ? `, ft ${line.footage}` : ''}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <ActivityTable
              testId="inventory-activity"
              rows={activityFromMovementLog(item.movementLog ?? [])}
            />
            <p className="mono-id hint" style={{ fontSize: '0.8rem', marginTop: '1rem' }}>
              Id: {item.id}
            </p>
          </>
        )}
      </div>
    </aside>
  );
}
